import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Widget } from "./Widget";
import VideoLibraryIcon from "assets/icons/livestreams.svg?react";
import styles from "./Widget/index.module.scss";
import { useGetPagedBroadcastByFilter } from "hooks/useGetPagedBroadcastByFilter";
import { BroadcastThumbnail } from "components/thumbnails/BroadcastThumbnail";
import { DetailProps } from "./Detail";
import { localizeTime } from "helpers/time";
import { DisabledVariant } from "components/entity-details/BroadcastDetails";

export const ScheduledLivestreamsWidget = () => {
    const { t } = useTranslation();

    const { firstLoading, upcomingLivestreams, error } =
        useGetPagedBroadcastByFilter({
            videoStatusType: "Livestream",
            itemsPerPage: 1,
            sortType: "StartDate"
        });

    const EmptyState = useMemo(() => {
        if (error) {
            return <p>{t("widgets:livestream-error")}</p>;
        }

        if (!upcomingLivestreams) return <></>;

        if (upcomingLivestreams.length === 0) {
            return <p>{t("widgets:no-scheduled-livestream")}</p>;
        }

        return <></>;
    }, [upcomingLivestreams, error, t]);

    const ScheduledLivestreamsWidgetContent = useMemo(() => {
        const nextStream = upcomingLivestreams[0];
        const startTime = nextStream?.Details?.StartsAt;
        const millisecondsUntilStart =
            new Date(startTime).getTime() - Date.now();

        return (
            <>
                <p>{localizeTime(startTime, "dddd, MMMM D [at] h:mm A")}</p>
                <Link
                    to={`/scheduled-livestreams/broadcast/${nextStream?.Details?.Id}`}
                >
                    <BroadcastThumbnail
                        thumbnailImageURL={nextStream?.Thumbnail?.Details?.Url}
                        allowVideoPlaybackOnThumbnailClick={false}
                        isScheduledUpload={false}
                        isScheduledLive={true}
                        disabled={false}
                        disabledVariant={DisabledVariant.NoInteract}
                        showPremiereCountdown={
                            nextStream?.Details?.ShowPremiereCountdown
                        }
                        scheduledSecondsRemaining={millisecondsUntilStart}
                    />
                </Link>
            </>
        );
    }, [upcomingLivestreams]);

    const bodyComponentObject = useMemo(() => {
        const hasScheduledLivestream = upcomingLivestreams.length > 0;

        const componentObject: DetailProps = {
            children: (
                <div className={styles["scheduled-livestreams-widget"]}>
                    {hasScheduledLivestream
                        ? ScheduledLivestreamsWidgetContent
                        : EmptyState}
                </div>
            )
        };

        if (hasScheduledLivestream)
            componentObject.title = t("widgets:next-livestream");

        return componentObject;
    }, [ScheduledLivestreamsWidgetContent, EmptyState, upcomingLivestreams, t]);

    return (
        <Widget
            loading={firstLoading}
            icon={<VideoLibraryIcon />}
            title={t("page-titles:scheduled-livestreams").toUpperCase()}
            details={[bodyComponentObject]}
            footerSubtitle={t("widgets:livestreams-footer")}
            button={
                <Link
                    className="btn btn-outline-secondary"
                    to={"/scheduled-livestreams"}
                >
                    {t("widgets:schedule-new")}
                </Link>
            }
        ></Widget>
    );
};
